import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_URL as BASE_API_URL } from "../../config/apiConfig";
import { getDriverToken } from "../../utils/driverAuthUtils";

// Use centralized API configuration
const API_URL = `${BASE_API_URL}/wallet`;

const initialState = {
  wallet: null,
  transactions: [],
  withdrawalRequest: null,
  bankDetails: null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
};

// Get driver wallet
export const getDriverWallet = createAsyncThunk(
  "wallet/getDriverWallet",
  async (driverId, thunkAPI) => {
    const maxRetries = 3;
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        const driverAuthState = thunkAPI.getState().driverAuth;

        // Use utility function to get token from multiple sources
        const token = getDriverToken(driverAuthState);

        if (!token) {
          return thunkAPI.rejectWithValue("Driver not authenticated");
        }


        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 15000, // 15 second timeout
        };

        const response = await axios.get(`${API_URL}/driver/${driverId}`, config);

        return response.data.data;
      } catch (error) {
        retryCount++;
        console.error(`Wallet API error (attempt ${retryCount}/${maxRetries}):`, error.message);

        // If it's a network error and we haven't exceeded retries, try again
        if (retryCount < maxRetries && (error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR' || !error.response)) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }


        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : error.message || "Network Error - Unable to load wallet";
        return thunkAPI.rejectWithValue(message);
      }
    }
  },
);

// Get driver transactions
export const getDriverTransactions = createAsyncThunk(
  "wallet/getDriverTransactions",
  async ({ driverId, page = 1, limit = 20 }, thunkAPI) => {
    const maxRetries = 3;
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        const driverAuthState = thunkAPI.getState().driverAuth;

        // Use utility function to get token from multiple sources
        const token = getDriverToken(driverAuthState);

        if (!token) {
          return thunkAPI.rejectWithValue("Driver not authenticated");
        }


        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 15000, // 15 second timeout
        };

        const requestUrl = `${API_URL}/driver/${driverId}/transactions?page=${page}&limit=${limit}`;

        const response = await axios.get(requestUrl, config);

        return response.data.data;
      } catch (error) {
        retryCount++;
        console.error(`Transactions API error (attempt ${retryCount}/${maxRetries}):`, error.message);

        // If it's a network error and we haven't exceeded retries, try again
        if (retryCount < maxRetries && (error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR' || !error.response)) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }


        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : error.message || "Network Error - Unable to load transactions";
        return thunkAPI.rejectWithValue(message);
      }
    }
  },
);

// Request withdrawal
export const requestWithdrawal = createAsyncThunk(
  "wallet/requestWithdrawal",
  async ({ driverId, amount, paymentMethod, accountDetails }, thunkAPI) => {
    try {
      const driverAuthState = thunkAPI.getState().driverAuth;
      const token = driverAuthState.token || driverAuthState.driver?.token;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        `${API_URL}/driver/request-withdrawal`,
        { driverId, amount, paymentMethod, accountDetails },
        config,
      );
      return response.data.data;
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;
      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Pay commission
export const payCommission = createAsyncThunk(
  "wallet/payCommission",
  async ({ driverId, amount, paymentMode, referenceId }, thunkAPI) => {
    try {
      const driverAuthState = thunkAPI.getState().driverAuth;
      const token = driverAuthState.token || driverAuthState.driver?.token;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        `${API_URL}/driver/pay-commission`,
        { driverId, amount, paymentMode, referenceId },
        config,
      );
      return response.data.data;
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;
      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Get user wallet (customer/user or corporate staff)
export const getUserWallet = createAsyncThunk(
  "wallet/getUserWallet",
  async (_, thunkAPI) => {
    try {
      const authState = thunkAPI.getState().auth;
      // Check for token from user or corporate sources
      const userToken = localStorage.getItem("token");
      const corporateToken = localStorage.getItem("corporateToken");

      const token = corporateToken || userToken;

      if (!token) {
        return thunkAPI.rejectWithValue("User not authenticated");
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      // Use appropriate endpoint based on token type
      const endpoint = corporateToken ? `${API_URL}/staff/balance` : `${API_URL}/user/balance`;
      console.log("🔍 Fetching wallet from:", endpoint);
      const response = await axios.get(endpoint, config);
      console.log("🔍 Wallet response:", response.data);
      return response.data.data;
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message || "Failed to fetch wallet balance";
      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Get user wallet transactions (or corporate staff transactions)
export const getUserTransactions = createAsyncThunk(
  "wallet/getUserTransactions",
  async ({ page = 1, limit = 20 }, thunkAPI) => {
    try {
      // Check for token from user or corporate sources
      const userToken = localStorage.getItem("token");
      const corporateToken = localStorage.getItem("corporateToken");

      const token = corporateToken || userToken;

      if (!token) {
        return thunkAPI.rejectWithValue("User not authenticated");
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      // Use appropriate endpoint based on token type
      const endpoint = corporateToken
        ? `${API_URL}/staff/transactions?page=${page}&limit=${limit}`
        : `${API_URL}/user/transactions?page=${page}&limit=${limit}`;

      const response = await axios.get(endpoint, config);
      return response.data.data;
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message || "Failed to fetch transactions";
      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Add money to wallet (topup) - user or corporate staff
export const addMoneyToWallet = createAsyncThunk(
  "wallet/addMoney",
  async ({ amount, razorpayOrderId, razorpayPaymentId }, thunkAPI) => {
    try {
      // Check for token from user or corporate sources
      const userToken = localStorage.getItem("token");
      const corporateToken = localStorage.getItem("corporateToken");

      const token = corporateToken || userToken;

      if (!token) {
        return thunkAPI.rejectWithValue("User not authenticated");
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      // Use appropriate endpoint based on token type
      const endpoint = corporateToken
        ? `${API_URL}/staff/add-money`
        : `${API_URL}/user/topup`;

      const response = await axios.post(
        endpoint,
        {
          amount,
          razorpayOrderId,
          razorpayPaymentId,
          paymentMethod: "razorpay"
        },
        config
      );
      return response.data.data;
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message || "Failed to add money to wallet";
      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Save bank details (user)
export const saveBankDetails = createAsyncThunk(
  "wallet/saveBankDetails",
  async ({ accountName, accountNumber, ifscCode, bankName }, thunkAPI) => {
    try {
      // For user wallet endpoints, only use the regular user token
      const token = localStorage.getItem("token");

      if (!token) {
        return thunkAPI.rejectWithValue("User not authenticated");
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        `${API_URL}/user/save-bank-details`,
        { accountName, accountNumber, ifscCode, bankName },
        config
      );
      return response.data.data;
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message || "Failed to save bank details";
      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Get bank details (user)
export const getBankDetails = createAsyncThunk(
  "wallet/getBankDetails",
  async (_, thunkAPI) => {
    try {
      // Check for both user and corporate tokens
      const userToken = localStorage.getItem("token");
      const corporateToken = localStorage.getItem("corporateToken");

      const token = corporateToken || userToken;

      if (!token) {
        return thunkAPI.rejectWithValue("User not authenticated");
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      // Use appropriate endpoint based on token type
      const endpoint = corporateToken
        ? `${API_URL}/staff/bank-details`
        : `${API_URL}/user/bank-details`;

      const response = await axios.get(endpoint, config);
      return response.data.data;
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message || "Failed to fetch bank details";
      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Request withdrawal (user)
export const requestUserWithdrawal = createAsyncThunk(
  "wallet/requestUserWithdrawal",
  async ({ amount, bankDetails }, thunkAPI) => {
    try {
      // For user wallet endpoints, only use the regular user token
      const token = localStorage.getItem("token");

      if (!token) {
        return thunkAPI.rejectWithValue("User not authenticated");
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        `${API_URL}/user/request-withdrawal`,
        { amount, bankDetails },
        config
      );
      return response.data.data;
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message || "Failed to request withdrawal";
      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Process wallet payment for booking
export const processWalletPayment = createAsyncThunk(
  "wallet/processWalletPayment",
  async ({ bookingId, amount }, thunkAPI) => {
    try {
      // For user wallet endpoints, only use the regular user token
      const token = localStorage.getItem("token");

      if (!token) {
        return thunkAPI.rejectWithValue("User not authenticated");
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        `${API_URL}/user/process-booking-payment`,
        { bookingId, amount },
        config
      );
      return response.data.data;
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message || "Failed to process wallet payment";
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isError = false;
      state.isSuccess = false;
      state.message = "";
    },
    clearWithdrawalRequest: (state) => {
      state.withdrawalRequest = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get driver wallet
      .addCase(getDriverWallet.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getDriverWallet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.wallet = action.payload;
      })
      .addCase(getDriverWallet.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Get driver transactions
      .addCase(getDriverTransactions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getDriverTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.transactions = action.payload.transactions;
      })
      .addCase(getDriverTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Request withdrawal
      .addCase(requestWithdrawal.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(requestWithdrawal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.withdrawalRequest = action.payload;
        state.message = "Withdrawal request submitted successfully";
        // Update wallet balance and pending withdrawal
        if (state.wallet) {
          state.wallet.pendingWithdrawal = action.payload;
        }
      })
      .addCase(requestWithdrawal.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Pay commission
      .addCase(payCommission.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(payCommission.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.message = "Commission payment processed successfully";
        // Update wallet balance
        if (state.wallet) {
          state.wallet.balance = action.payload.newBalance;
        }
      })
      .addCase(payCommission.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Get user wallet
      .addCase(getUserWallet.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserWallet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.wallet = action.payload;
      })
      .addCase(getUserWallet.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Get user transactions
      .addCase(getUserTransactions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.transactions = action.payload.transactions || action.payload;
      })
      .addCase(getUserTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Add money to wallet
      .addCase(addMoneyToWallet.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addMoneyToWallet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.message = "Money added to wallet successfully";
        // Update wallet balance
        if (state.wallet) {
          state.wallet.balance = action.payload.newBalance || action.payload.balance;
        }
      })
      .addCase(addMoneyToWallet.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Save bank details
      .addCase(saveBankDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(saveBankDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.message = "Bank details saved successfully";
        state.bankDetails = action.payload;
      })
      .addCase(saveBankDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Get bank details
      .addCase(getBankDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getBankDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.bankDetails = action.payload;
      })
      .addCase(getBankDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Request user withdrawal
      .addCase(requestUserWithdrawal.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(requestUserWithdrawal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.message = "Withdrawal request submitted successfully";
        state.withdrawalRequest = action.payload;
        // Update wallet balance
        if (state.wallet && action.payload.newBalance !== undefined) {
          state.wallet.balance = action.payload.newBalance;
        }
        // Bank details are automatically saved on withdrawal, so they will persist
        // on next getBankDetails fetch when withdrawal modal is opened again
      })
      .addCase(requestUserWithdrawal.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Process wallet payment
      .addCase(processWalletPayment.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(processWalletPayment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.message = "Payment processed successfully";
        // Update wallet balance
        if (state.wallet) {
          state.wallet.balance = action.payload.newBalance || action.payload.balance;
        }
      })
      .addCase(processWalletPayment.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  },
});

export const { reset, clearWithdrawalRequest } = walletSlice.actions;
export default walletSlice.reducer;
